import { useState } from "react";
import { getTaxRate, printNumber, splitDate, yearRange } from "src/utils";
import { getSpendingItemOverYears } from "./calculate";
import { Client, Income, IncomeMapData, ScenarioSettings } from "src/types";
import calculate from "src/calculator/calculate";
import { useFullscreen } from "src/hooks/useFullScreen";
import CurrentSpendingModal from "./Modals/CurrentSpendingModal";
import OldSpendingModal from "./Modals/OldSpendingModal";
import NewSpendingModal from "./Modals/NewSpendingModal";
import { useUser } from "src/hooks/useUser";

interface SpendingTableProps {
  settings: ScenarioSettings;
  client: Client;
  print?: boolean;
}

const SpendingTable = ({ settings, client, print }: SpendingTableProps) => {
  const spending = client.spending;
  const [selectedCol, setSelectedCol] = useState(null as any);
  const [selectedRow, setSelectedRow] = useState(null as any);
  const [spendingOpen, setSpendingOpen] = useState({} as any);
  const currentYear = new Date().getFullYear();
  const { isFullscreen } = useFullscreen();
  const { user } = useUser();
  const factor = settings.monthlyYearly === "monthly" ? 12 : 1;
  const data: IncomeMapData = {
    people: client.people,
    incomes: settings.incomes,
    version: 1,
  };

  const [timer, setTimer] = useState<any>(0);

  const baseSpending = getSpendingItemOverYears(
    client.people,
    spending,
    settings,
    currentYear,
    currentYear + settings.maxYearsShown,
    "base",
  );
  const preSpending = spending.preSpending.map((item) =>
    getSpendingItemOverYears(
      client.people,
      spending,
      settings,
      currentYear,
      currentYear + settings.maxYearsShown,
      "pre",
      item.category,
    ),
  );

  const postSpending = spending.postSpending.map((item) =>
    getSpendingItemOverYears(
      client.people,
      spending,
      settings,
      currentYear,
      currentYear + settings.maxYearsShown,
      "post",
      item.category,
    ),
  );
  const results = [baseSpending, ...preSpending, ...postSpending];
  return (
    <div className="flex gap-4 w-full">
      <CurrentSpendingModal
        open={spendingOpen.type == "current"}
        setOpen={() => setSpendingOpen({})}
      />
      {spending.preSpending.map((_, index) => (
        <OldSpendingModal
          open={spendingOpen.type == "pre" && spendingOpen.index === index}
          setOpen={() => setSpendingOpen({})}
          index={index}
          settings={settings}
        />
      ))}
      {spending.postSpending.map((_, index) => (
        <NewSpendingModal
          open={spendingOpen.type == "post" && spendingOpen.index === index}
          setOpen={() => setSpendingOpen({})}
          index={index}
          settings={settings}
        />
      ))}
      <div className="w-full">
        <table className="w-full border print:border-none bg-white">
          <thead
            className={`text-xs cursor-pointer bg-[#F9FAFB] text-black ${print ? "font-normal" : "font-medium"} text-left sticky ${print ? "" : isFullscreen ? "top-[200px]" : "top-[270px]"} z-50 print:border-b-gray-500 print:border-b-2 border-black`}
            style={{ userSelect: "none" }}
          >
            <tr className="border-b border-black">
              <th
                className={`px-6 print:px-3 py-3 ${selectedCol === "year" ? "bg-slate-200" : ""}  ${print ? "font-medium" : "font-medium"}`}
                onClick={() =>
                  setSelectedCol(selectedCol === "year" ? null : "year")
                }
              >
                Year
              </th>
              <th
                className={`px-6 print:px-3 py-3 ${selectedCol === "age" ? "bg-slate-200" : ""} ${print ? "font-medium" : "font-medium"}`}
                onClick={() =>
                  setSelectedCol(selectedCol === "age" ? null : "age")
                }
              >
                Age
              </th>
              <th
                className={`px-6 print:px-3 py-3 ${selectedCol === "base" ? "bg-slate-200" : ""} ${print ? "font-medium" : "font-medium"}`}
                onClick={(e) => {
                  if (e.detail === 1) {
                    setTimer(
                      setTimeout(() => {
                        setSelectedCol(selectedCol === "base" ? null : "base");
                      }, 200),
                    );
                  }
                  if (e.detail === 2) {
                    clearTimeout(timer);
                    e.preventDefault();
                    e.stopPropagation();
                    setSpendingOpen({ type: "current" });
                  }
                }}
              >
                Base Spending
              </th>
              {spending.preSpending.map((i, index) => (
                <th
                  className={`px-6 print:px-3 py-3 ${selectedCol === `pre-${i.category}` ? "bg-slate-200" : ""} ${print ? "font-medium" : "font-medium"}`}
                  onClick={(e) => {
                    if (e.detail === 1) {
                      setTimer(
                        setTimeout(() => {
                          setSelectedCol(
                            selectedCol === `pre-${i.category}`
                              ? null
                              : `pre-${i.category}`,
                          );
                        }, 200),
                      );
                    }
                    if (e.detail === 2) {
                      clearTimeout(timer);
                      e.preventDefault();
                      e.stopPropagation();
                      setSpendingOpen({ type: "pre", index: index });
                    }
                  }}
                >
                  {i.category}
                </th>
              ))}
              {spending.postSpending.map((i, index) => (
                <th
                  className={`px-6 print:px-3 py-3 ${selectedCol === `post-${i.category}` ? "bg-slate-200" : ""} ${print ? "font-medium" : "font-medium"}`}
                  onClick={(e) => {
                    if (e.detail === 1) {
                      setTimer(
                        setTimeout(() => {
                          setSelectedCol(
                            selectedCol === `post-${i.category}`
                              ? null
                              : `post-${i.category}`,
                          );
                        }, 200),
                      );
                    }
                    if (e.detail === 2) {
                      clearTimeout(timer);
                      e.preventDefault();
                      e.stopPropagation();
                      setSpendingOpen({ type: "post", index: index });
                    }
                  }}
                >
                  {i.category}
                </th>
              ))}
              {user?.info?.taxesFlag && (
                <th
                  className={`px-6 print:px-3 py-3 ${selectedCol === "taxes" ? "bg-slate-200" : ""} ${print ? "font-medium" : "font-medium"}`}
                  onClick={() =>
                    setSelectedCol(selectedCol === "taxes" ? null : "taxes")
                  }
                >
                  Taxes
                </th>
              )}
              <th
                className={`px-6 print:px-3 py-3 ${selectedCol === "total" ? "bg-slate-200" : ""} ${print ? "font-medium" : "font-medium"}`}
                onClick={() =>
                  setSelectedCol(selectedCol === "total" ? null : "total")
                }
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody className={`${"text-[14px]"} inconsolata`}>
            {yearRange(currentYear, currentYear + settings.maxYearsShown).map(
              (year, ii) => (
                <tr
                  className={`${selectedRow === year ? "bg-slate-200" : "hover:bg-slate-100"} ${ii % 2 == 1 ? "bg-[#F9FAFB]" : ""} border-y border-[#EAECF0] inconsolata`}
                  onClick={() =>
                    setSelectedRow(selectedRow === year ? null : year)
                  }
                >
                  <td
                    className={`px-6 print:px-3 py-[0.4rem] print:py-[0.2rem] inconsolata font-medium ${selectedCol === "year" ? "bg-slate-200" : ""}`}
                  >
                    {year}
                  </td>
                  <td
                    className={`px-6 print:px-3 py-1 print:py-[0.2rem] inconsolata font-medium ${selectedCol === "age" ? "bg-slate-200" : ""}`}
                  >
                    {data.people
                      .map((p) => year - splitDate(p.birthday).year)
                      .join("/")}
                  </td>
                  <td
                    className={`px-6 print:px-3 py-1 print:py-[0.2rem] text-[#475467] ${selectedCol === "base" && "bg-slate-200"}`}
                  >
                    {printNumber(
                      baseSpending[year - currentYear].amount / factor,
                    )}
                  </td>
                  {preSpending.map((item, index) => (
                    <td
                      className={`px-6 print:px-3 py-1 print:py-[0.2rem] text-[#475467] ${
                        selectedCol?.startsWith("pre-") &&
                        selectedCol ===
                          `pre-${spending.preSpending[index].category}`
                          ? "bg-slate-200"
                          : ""
                      }`}
                    >
                      {printNumber(item[year - currentYear].amount / factor)}
                    </td>
                  ))}
                  {postSpending.map((item, index) => (
                    <td
                      className={`px-6 print:px-3 py-1 print:py-[0.2rem] text-[#475467] ${
                        selectedCol?.startsWith("post-") &&
                        selectedCol ===
                          `post-${spending.postSpending[index].category}`
                          ? "bg-slate-200"
                          : ""
                      }`}
                    >
                      {printNumber(item[year - currentYear].amount / factor)}
                    </td>
                  ))}

                  {user?.info?.taxesFlag &&
                    (() => {
                      const calculateOne = (
                        income: Income,
                        currentYear: number,
                      ) => {
                        const result = calculate({
                          people: client.people,
                          income,
                          currentYear,
                          scenario: settings,
                        });

                        return result.amount;
                      };
                      const income = settings.incomes
                        .filter((income) => income.enabled)
                        .map((income) => calculateOne(income, year))
                        .filter((t) => typeof t === "number")
                        .reduce((a, b) => a + b, 0);

                      const taxRate = getTaxRate(user, settings, year);

                      const taxes = (income * taxRate) / factor;
                      return (
                        <td
                          className={`px-6 py-1 print:px-3 text-[#475467] ${selectedCol === "taxes" ? "bg-slate-200" : ""}`}
                        >
                          {printNumber(taxes)}
                        </td>
                      );
                    })()}
                  <td
                    className={`px-6 py-1 print:py-[0.2rem] print:px-3 font-medium ${selectedCol === "total" ? "bg-slate-200" : ""}`}
                  >
                    {printNumber(
                      results
                        .map((item) => item[year - currentYear].amount)
                        .reduce((a, b) => a + b, 0) / factor,
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SpendingTable;
